// HOPS Rating widget (Players canvas) — the player's HOPS rating placed on the
// population distribution. Old Catalyst showed this on the Bio tab; here it's its
// own widget. No `soccer-vis` (§7B decision 1): the curve is hand-drawn SVG.
//
// The maths (percentile lookup, curve geometry) live in the pure, unit-tested
// `hopsCurve.ts`; this file is the view. Consumes player + competition:
//  - rating   ← GET /player  (player_metrics.HOPS.rating)
//  - the curve ← GET /hops-distribution, gendered by the SCOPED competition's
//    competitionWomens flag (Option A: follow the filter bar), defaulting men's
//    when it can't be resolved.

import { usePlayerBio, usePlayerCompetitions, useHopsDistribution } from "../data/players.js";
import type { WidgetProps } from "./registry.js";
import { theme } from "../lib/theme.js";
import {
  parseDistribution,
  computePercentile,
  buildHopsCurve,
  ordinal,
  type HopsCurve,
} from "./hopsCurve.js";

// SVG drawing size (viewBox units). The <svg> scales to the tile width; these
// just fix the internal geometry the pure module computes against.
const CURVE_W = 280;
const CURVE_H = 90;

export default function HopsWidget({ scope }: WidgetProps) {
  const { playerId, competitionId } = scope;
  const bio = usePlayerBio(playerId);
  const comps = usePlayerCompetitions(playerId);

  // Gender of the curve = the scoped competition's flag (Option A). Default men's
  // (false) when unresolved — the same default old Catalyst falls back to.
  const scopedComp = comps.data?.competitions.find((c) => c.id === competitionId);
  const isWomen = scopedComp?.competitionWomens ?? false;
  const dist = useHopsDistribution(playerId !== null ? isWomen : null);

  if (playerId === null) return <Msg>Search for a player above.</Msg>;
  if (bio.isPending) return <Msg>Loading…</Msg>;
  if (bio.isError) return <Msg danger>Couldn't load HOPS. {String(bio.error)}</Msg>;

  const hops = bio.data.player.hops;
  if (!hops) return <Msg>No HOPS rating for this player.</Msg>;

  const parsed = parseDistribution(dist.data);
  const percentile = parsed ? computePercentile(hops.rating, parsed.percentiles) : null;
  const curve = parsed ? buildHopsCurve(parsed.buckets, hops.rating, CURVE_W, CURVE_H) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
        <div
          style={{
            color: theme.ink,
            fontSize: 40,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1,
          }}
        >
          {hops.rating.toFixed(3)}
        </div>
        <div style={{ color: theme.inkMuted, fontSize: 13 }}>
          <div style={{ textTransform: "uppercase", letterSpacing: 0.4, fontSize: 11 }}>
            HOPS Rating
          </div>
          <div style={{ color: theme.accent, fontSize: 14, marginTop: 2 }}>
            {percentile !== null ? `${ordinal(percentile)} percentile` : "percentile —"}
            {isWomen && <span style={{ color: theme.inkFaint }}> · women&rsquo;s</span>}
          </div>
        </div>
      </div>

      {percentile !== null && <PercentileBar percentile={percentile} />}

      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
        {dist.isPending && <Msg>Loading distribution…</Msg>}
        {!dist.isPending && !curve && <Msg>Distribution unavailable.</Msg>}
        {curve && <Curve curve={curve} rating={hops.rating} />}
      </div>
    </div>
  );
}

/** A 0–100 track filled to the player's percentile — the quick "where do they sit". */
function PercentileBar({ percentile }: { percentile: number }) {
  return (
    <div>
      <div style={{ position: "relative", height: 8, background: theme.field, borderRadius: 4 }}>
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: `${percentile}%`,
            background: theme.accent,
            borderRadius: 4,
          }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", color: theme.inkFaint, fontSize: 10, marginTop: 3 }}>
        <span>0</span>
        <span>50</span>
        <span>100</span>
      </div>
    </div>
  );
}

/** The distribution curve: filled area + top line, with a vertical marker at the
 *  player's rating. Rating axis min/max labelled at the ends. */
function Curve({ curve, rating }: { curve: HopsCurve; rating: number }) {
  return (
    <div>
      <svg
        viewBox={`0 0 ${curve.width} ${curve.height}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: 90, display: "block" }}
        role="img"
        aria-label={`HOPS distribution with a marker at ${rating.toFixed(3)}`}
      >
        <defs>
          <linearGradient id="hopsFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.accent} stopOpacity={0.45} />
            <stop offset="100%" stopColor={theme.accent} stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <path d={curve.areaPath} fill="url(#hopsFill)" />
        <path d={curve.linePath} fill="none" stroke={theme.accent} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        {/* Player's rating marker. */}
        <line
          x1={curve.indicatorX}
          y1={curve.topY}
          x2={curve.indicatorX}
          y2={curve.baselineY}
          stroke={theme.ink}
          strokeWidth={1.5}
          strokeDasharray="3 2"
          vectorEffect="non-scaling-stroke"
        />
        <circle cx={curve.indicatorX} cy={curve.topY} r={2.5} fill={theme.ink} />
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", color: theme.inkFaint, fontSize: 10, marginTop: 3, fontVariantNumeric: "tabular-nums" }}>
        <span>{curve.domainMin.toFixed(2)}</span>
        <span style={{ color: theme.inkMuted }}>rating distribution</span>
        <span>{curve.domainMax.toFixed(2)}</span>
      </div>
    </div>
  );
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
